import { useState } from 'react'
import {
  Stack, Text, Table, Button, Badge, Modal,
  Select, NumberInput, Group, Switch, Alert, ActionIcon,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconTrash, IconEdit, IconAlertCircle } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useCurrentTenant } from '@/store/auth'
import { DataCard } from '@/components/ui'

interface AlertRule {
  id: number
  tenant_id: number
  alert_source: string
  condition: string
  category_id: number | null
  default_priority: string
  auto_assignee_user_id: number | null
  cooldown_minutes: number
  is_active: boolean
}

interface AlertRulePayload {
  alert_source: string
  condition: string
  category_id?: number
  default_priority: string
  auto_assignee_user_id?: number
  cooldown_minutes: number
  is_active: boolean
}

const CONDITION_MAP: Record<string, { value: string; label: string }[]> = {
  budget: [{ value: 'budget_exceeded', label: 'Budget Exceeded' }],
  ec2_health: [
    { value: 'instance_impaired', label: 'Instance Impaired' },
    { value: 'instance_stopped', label: 'Instance Stopped' },
  ],
  cloudwatch: [
    { value: 'cpu_gt_90', label: 'CPU > 90%' },
    { value: 'disk_gt_85', label: 'Disco > 85%' },
    { value: 'memory_gt_90', label: 'Memória > 90%' },
  ],
}

const SOURCE_OPTIONS = [
  { value: 'budget', label: 'Budget' },
  { value: 'ec2_health', label: 'EC2 Health' },
  { value: 'cloudwatch', label: 'CloudWatch' },
]

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'critical', label: 'Crítica' },
]

const PRIORITY_COLOR: Record<string, string> = {
  low: 'blue',
  medium: 'yellow',
  high: 'orange',
  critical: 'red',
}

const SOURCE_COLOR: Record<string, string> = {
  budget: 'violet',
  ec2_health: 'teal',
  cloudwatch: 'indigo',
}

function RuleModal({
  opened, onClose, initial, tenantId,
}: {
  opened: boolean
  onClose: () => void
  initial: AlertRule | null
  tenantId: number | undefined
}) {
  const queryClient = useQueryClient()
  const isEdit = !!initial

  const [alertSource, setAlertSource] = useState(initial?.alert_source ?? 'budget')
  const [condition, setCondition] = useState(initial?.condition ?? 'budget_exceeded')
  const [categoryId, setCategoryId] = useState<number | string>(initial?.category_id ?? '')
  const [priority, setPriority] = useState(initial?.default_priority ?? 'medium')
  const [assigneeId, setAssigneeId] = useState<number | string>(initial?.auto_assignee_user_id ?? '')
  const [cooldown, setCooldown] = useState<number | string>(initial?.cooldown_minutes ?? 60)
  const [isActive, setIsActive] = useState(initial?.is_active ?? true)

  const mutation = useMutation({
    mutationFn: async (payload: AlertRulePayload) => {
      if (isEdit && initial) {
        await api.put(`/v1/tenants/${tenantId}/ticket-alert-rules/${initial.id}`, payload)
      } else {
        await api.post(`/v1/tenants/${tenantId}/ticket-alert-rules`, payload)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-alert-rules', tenantId] })
      notifications.show({ color: 'green', message: `Regra ${isEdit ? 'atualizada' : 'criada'} com sucesso!` })
      onClose()
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Erro: ${err.message}` })
    },
  })

  const handleSourceChange = (v: string | null) => {
    const src = v ?? 'budget'
    setAlertSource(src)
    const conditions = CONDITION_MAP[src]
    if (conditions?.length) {
      setCondition(conditions[0].value)
    }
  }

  const handleSubmit = () => {
    if (!alertSource || !condition) {
      notifications.show({ color: 'orange', message: 'Fonte e condição são obrigatórios.' })
      return
    }
    mutation.mutate({
      alert_source: alertSource,
      condition,
      category_id: categoryId ? Number(categoryId) : undefined,
      default_priority: priority,
      auto_assignee_user_id: assigneeId ? Number(assigneeId) : undefined,
      cooldown_minutes: Number(cooldown) || 60,
      is_active: isActive,
    })
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{isEdit ? 'Editar Regra de Alerta' : 'Nova Regra de Alerta'}</Text>}
      size="lg"
    >
      <Stack gap="sm">
        <Group grow>
          <Select
            label="Fonte do alerta"
            required
            value={alertSource}
            onChange={handleSourceChange}
            data={SOURCE_OPTIONS}
          />
          <Select
            label="Condição"
            required
            value={condition}
            onChange={(v) => setCondition(v ?? '')}
            data={CONDITION_MAP[alertSource] ?? []}
          />
        </Group>
        <Group grow>
          <Select
            label="Prioridade padrão"
            value={priority}
            onChange={(v) => setPriority(v ?? 'medium')}
            data={PRIORITY_OPTIONS}
          />
          <NumberInput
            label="Cooldown (minutos)"
            min={1}
            value={cooldown}
            onChange={setCooldown}
          />
        </Group>
        <Group grow>
          <NumberInput
            label="ID da categoria (opcional)"
            placeholder="Ex: 1"
            min={1}
            value={categoryId}
            onChange={setCategoryId}
          />
          <NumberInput
            label="ID do responsável (opcional)"
            placeholder="Ex: 10"
            min={1}
            value={assigneeId}
            onChange={setAssigneeId}
          />
        </Group>
        {isEdit && (
          <Switch
            label="Regra ativa"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={mutation.isPending}>Cancelar</Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>Salvar</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export function AlertaRules() {
  const tenantId = useCurrentTenant()?.id
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editRule, setEditRule] = useState<AlertRule | null>(null)

  const { data: rules = [], isLoading, error } = useQuery<AlertRule[]>({
    queryKey: ['ticket-alert-rules', tenantId],
    queryFn: async () => {
      const res = await api.get<AlertRule[]>(`/v1/tenants/${tenantId}/ticket-alert-rules`)
      return res.data
    },
    enabled: !!tenantId,
  })

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) =>
      api.put(`/v1/tenants/${tenantId}/ticket-alert-rules/${id}`, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-alert-rules', tenantId] })
    },
    onError: () => {
      notifications.show({ color: 'red', message: 'Erro ao alterar status da regra.' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) =>
      api.delete(`/v1/tenants/${tenantId}/ticket-alert-rules/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-alert-rules', tenantId] })
      notifications.show({ color: 'green', message: 'Regra removida.' })
    },
    onError: () => {
      notifications.show({ color: 'red', message: 'Erro ao remover regra.' })
    },
  })

  const handleNew = () => { setEditRule(null); setModalOpen(true) }
  const handleEdit = (rule: AlertRule) => { setEditRule(rule); setModalOpen(true) }

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar as regras de alerta. Tente novamente em instantes.
        </Alert>
      )}

      <DataCard flush
        title="Regras de Alerta para Chamados"
        accent="primary"
        badge={{ value: rules.length, color: 'blue' }}
        loading={isLoading}
        extra={
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={handleNew} disabled={!tenantId}>
            Nova Regra
          </Button>
        }
      >
        {rules.length === 0 ? (
          <Text c="dimmed" size="sm" py="xl" px="md" ta="center">Nenhuma regra de alerta cadastrada. Clique em &quot;Nova Regra&quot; para configurar alertas automáticos.</Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Fonte</Table.Th>
                <Table.Th>Condição</Table.Th>
                <Table.Th>Prioridade Padrão</Table.Th>
                <Table.Th>Cooldown (min)</Table.Th>
                <Table.Th>Ativo</Table.Th>
                <Table.Th>Ações</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rules.map((rule) => (
                <Table.Tr key={rule.id}>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={SOURCE_COLOR[rule.alert_source] ?? 'gray'}>
                      {rule.alert_source}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="sm">{rule.condition}</Text></Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={PRIORITY_COLOR[rule.default_priority] ?? 'gray'}>
                      {rule.default_priority}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="sm">{rule.cooldown_minutes}</Text></Table.Td>
                  <Table.Td>
                    <Switch
                      checked={rule.is_active}
                      onChange={(e) => toggleMutation.mutate({ id: rule.id, is_active: e.target.checked })}
                      size="sm"
                    />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon variant="subtle" color="blue" onClick={() => handleEdit(rule)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => deleteMutation.mutate(rule.id)}
                        loading={deleteMutation.isPending}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </DataCard>

      <RuleModal
        key={editRule?.id ?? 'new'}
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        initial={editRule}
        tenantId={tenantId ? Number(tenantId) : undefined}
      />
    </Stack>
  )
}
